<template>
    <div class="team-compare">
        <div class="team-compare__subtitle">Ομαδικά στατιστικά</div>

        <div class="team-compare__teams">
            <router-link v-if="match.team1" :to="teamRoute(match.team1)" class="tc-team">
                <span class="tc-team__logo">
                    <img v-if="match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" alt="">
                    <v-icon v-else color="orange">mdi-basketball</v-icon>
                </span>
                <span class="tc-team__name">{{ match.team1.name }}</span>
            </router-link>
            <router-link v-if="match.team2" :to="teamRoute(match.team2)" class="tc-team tc-team--right">
                <span class="tc-team__name">{{ match.team2.name }}</span>
                <span class="tc-team__logo">
                    <img v-if="match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" alt="">
                    <v-icon v-else color="orange">mdi-basketball</v-icon>
                </span>
            </router-link>
        </div>

        <div v-for="row in rows" :key="row.key" class="tc-row">
            <div class="tc-value" :class="{ 'tc-value--lead': row.lead === 1 }">
                {{ row.display1 }}
                <small v-if="row.detail1">{{ row.detail1 }}</small>
            </div>
            <div class="tc-middle">
                <span class="tc-label">{{ row.label }}</span>
                <div class="tc-bars">
                    <div class="tc-bar tc-bar--left">
                        <span :style="{ width: `${row.share1}%`, background: color1 }" />
                    </div>
                    <div class="tc-bar">
                        <span :style="{ width: `${row.share2}%`, background: color2 }" />
                    </div>
                </div>
            </div>
            <div class="tc-value tc-value--right" :class="{ 'tc-value--lead': row.lead === 2 }">
                {{ row.display2 }}
                <small v-if="row.detail2">{{ row.detail2 }}</small>
            </div>
        </div>
    </div>
</template>

<script>
    import { getTeamColors } from '@/utils/shotChart'

    const STATS = [
        { key: 'points', label: 'Πόντοι' },
        { key: 'two', label: 'Δίποντα', made: 'twoPointsMade', attempts: 'twoPointAttempts' },
        { key: 'three', label: 'Τρίποντα', made: 'threePointsMade', attempts: 'threePointAttempts' },
        { key: 'free', label: 'Βολές', made: 'freeThrowsMade', attempts: 'freeThrowAttempts' },
        { key: 'offensiveRebounds', label: 'Επιθ. ριμπάουντ' },
        { key: 'secondChancePoints', label: 'Πόντοι 2ης ευκαιρίας' },
        { key: 'defensiveRebounds', label: 'Αμυντ. ριμπάουντ' },
        { key: 'rebounds', label: 'Ριμπάουντ' },
        { key: 'assists', label: 'Ασίστ' },
        { key: 'assistPoints', label: 'Πόντοι από ασίστ' },
        { key: 'steals', label: 'Κλεψίματα' },
        { key: 'pointsOffTurnovers', label: 'Πόντοι από λάθη αντιπάλου' },
        { key: 'blockedShots', label: 'Κοψίματα' },
        // fewer is better
        { key: 'turnovers', label: 'Λάθη', lowerIsBetter: true },
        // every foul: personal, technical and unsportsmanlike
        { key: 'fouls', label: 'Φάουλ', lowerIsBetter: true }
    ]

    const SUM_KEYS = ['points', 'twoPointsMade', 'twoPointAttempts', 'threePointsMade', 'threePointAttempts', 'freeThrowsMade', 'freeThrowAttempts',
                      'offensiveRebounds', 'defensiveRebounds', 'rebounds', 'assists', 'assistPoints', 'secondChancePoints', 'pointsOffTurnovers', 'steals', 'blockedShots', 'turnovers', 'personalFouls',
                      'technicalFouls', 'unsportsmanlikeFouls', 'disruptiveFouls', 'flagrantFouls']

    // Team totals side by side: bars grow from the middle, the better value of each row is highlighted
    export default {
        props: {
            match: {
                type: Object,
                default: null
            },
            mvp: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            totals1() {
                return this.sumTeam(this.match && this.match.team1)
            },
            totals2() {
                return this.sumTeam(this.match && this.match.team2)
            },
            teamColors() {
                return getTeamColors(this.match && this.match.team1, this.match && this.match.team2)
            },
            color1() {
                return this.teamColors.team1
            },
            color2() {
                return this.teamColors.team2
            },
            rows() {
                return STATS.map(stat => {
                    let value1
                    let value2
                    let display1
                    let display2
                    let detail1 = null
                    let detail2 = null
                    if (stat.made) {
                        value1 = this.percentage(this.totals1[stat.made], this.totals1[stat.attempts])
                        value2 = this.percentage(this.totals2[stat.made], this.totals2[stat.attempts])
                        display1 = `${value1}%`
                        display2 = `${value2}%`
                        detail1 = `${this.totals1[stat.made]}/${this.totals1[stat.attempts]}`
                        detail2 = `${this.totals2[stat.made]}/${this.totals2[stat.attempts]}`
                    } else {
                        value1 = this.totals1[stat.key]
                        value2 = this.totals2[stat.key]
                        display1 = value1
                        display2 = value2
                    }
                    const total = value1 + value2
                    let lead = 0
                    if (value1 !== value2) {
                        lead = (value1 > value2) !== !!stat.lowerIsBetter ? 1 : 2
                    }
                    return {
                        key: stat.key,
                        label: stat.label,
                        display1,
                        display2,
                        detail1,
                        detail2,
                        share1: total ? Math.round(value1 / total * 100) : 0,
                        share2: total ? Math.round(value2 / total * 100) : 0,
                        lead
                    }
                })
            }
        },
        methods: {
            sumTeam(team) {
                const totals = {}
                SUM_KEYS.forEach(key => { totals[key] = 0 })
                const players = (team && Array.isArray(team.players)) ? team.players : []
                players.forEach(player => {
                    const stats = player.stats && player.stats[0]
                    if (!stats) return
                    SUM_KEYS.forEach(key => { totals[key] += Number(stats[key]) || 0 })
                })
                totals.fouls = totals.personalFouls + totals.technicalFouls + totals.unsportsmanlikeFouls + totals.disruptiveFouls + totals.flagrantFouls
                return totals
            },
            percentage(made, attempts) {
                return attempts ? Math.round(made / attempts * 100) : 0
            },
            teamRoute(team) {
                return team && team.id ? { name: 'Team', params: { id: team.id } } : {}
            }
        }
    }
</script>

<style>
    .v-tabs:not(.v-tabs--vertical).v-tabs--right > .v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes) .v-slide-group__next,
    .v-tabs:not(.v-tabs--vertical):not(.v-tabs--right) > .v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes) .v-slide-group__prev {
        display: none;
    }

    .v-application--is-ltr .v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
    .v-application--is-ltr .v-data-table > .v-data-table__wrapper > table > tfoot > tr > th,
    .v-application--is-ltr .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
        text-align: center !important;
    }
</style>

<style scoped>
.team-compare {
    padding: 16px 18px 10px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.team-compare__subtitle {
    margin-bottom: 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.team-compare__teams {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.tc-team {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: #2b2b2e !important;
    text-decoration: none;
}

.tc-team--right {
    justify-content: flex-end;
    text-align: right;
}

.tc-team__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 3px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

.tc-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.tc-team__name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.1rem;
    font-weight: 800;
    line-height: 1.15;
}

.tc-team:hover .tc-team__name {
    color: #ef4444;
}

/* ---------- Rows ---------- */
.tc-row {
    display: grid;
    grid-template-columns: 64px 1fr 64px;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.tc-row:last-child {
    border-bottom: none;
}

.tc-value {
    display: flex;
    flex-direction: column;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
}

.tc-value--right {
    align-items: flex-end;
    text-align: right;
}

.tc-value small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
    font-weight: 600;
}

.tc-value--lead {
    color: #111;
    font-size: 1.15rem;
    font-weight: 800;
}

.tc-middle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 0;
}

.tc-label {
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.tc-bars {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    width: 100%;
}

.tc-bar {
    display: flex;
    height: 8px;
    border-radius: 4px;
    background: #f1f1f3;
    overflow: hidden;
}

/* the left bar grows from the middle towards the left */
.tc-bar--left {
    justify-content: flex-end;
}

.tc-bar span {
    display: block;
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease;
}

@media (max-width: 600px) {
    .team-compare {
        padding: 14px 12px 8px;
    }

    .tc-row {
        grid-template-columns: 52px 1fr 52px;
        gap: 8px;
    }

    .tc-team__name {
        font-size: 0.95rem;
    }
}
</style>
